import { useInvestmentAccountActivity } from '@egw/api'
import { Link } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router'
import { TransactionInvestmentItem } from '../../components/transaction-investment-item/transaction-investment-item'

export interface RecentInvestmentActivityProps {
  alias: number
}

export const RecentInvestmentActivity: FC<RecentInvestmentActivityProps> = ({ alias }) => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()
  const { data: investmentAccountActivity, isLoading, isError } = useInvestmentAccountActivity({ alias })

  const activity = investmentAccountActivity?.activity ?? []

  if (isLoading) {
    return (
      <div className="egw-container">
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton className="investments-activity-skeleton" count={5} height="50px" />
        </SkeletonTheme>
      </div>
    )
  }

  return (
    <div className="egw-container">
      {activity.length > 0 ? (
        <>
          <div className="flex gap-2 mb-6 items-baseline">
            <h3 className="typo-primary-xl-light text-text-header flex-1 leading-8 md:leading-10">
              {t('recent_activity.title')}
            </h3>
            <Link
              className="typo-primary-sm-semibold text-text-action"
              href={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY.path.replace(':alias', alias.toString())}
            >
              {t('recent_activity.all_activity')}
            </Link>
          </div>
          <div>
            {activity.slice(0, 5).map((transaction) => (
              <TransactionInvestmentItem key={transaction.activityId} alias={alias} transaction={transaction} />
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-row gap-2xs">
          <div className="rounded-full p-2xs bg-background-light h-fit flex items-center justify-center mr-3xs">
            <ClipboardDocumentListIcon className="w-7 h-7 text-text-icons-full flex-shrink-0 self-start" />
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-text-header typo-primary-base-semibold ">{t('recent_activity.no_recent_activity')}</p>
          </div>
        </div>
      )}

      {isError ? (
        <>
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('activity.error')}</p>
        </>
      ) : null}
    </div>
  )
}
